import React from "react";
import { useLocation, Link } from "react-router-dom";
import Icon from "../../icons/Icon/Icon";
import Text from "../../type/Text/Text";

export type Crumb = {
  name: string | React.ReactNode;
  route: string;
  search?: string;
  childRoutes?: Crumb[];
};

type BreadcrumbsProps = Readonly<{
  showBackArrow?: boolean;
  crumbs: Crumb[];
}>;

export default function Breadcrumbs({
  crumbs,
  showBackArrow,
}: BreadcrumbsProps) {
  const location = useLocation();
  const matchedPath =
    generateFlatBreadcrumbPath(crumbs, location.pathname) ?? [];

  return (
    <nav className="flex items-center" data-testid="breadcrumbs">
      {matchedPath.map((crumb, idx) => (
        <React.Fragment key={crumb.route}>
          <BreadcrumbItem
            crumb={
              showBackArrow && idx === 0
                ? {
                    ...crumb,
                    name: (
                      <div className="flex items-center">
                        <Icon
                          name="caret-left"
                          className="mb-1 mr-4 !h-[14px] !w-[9px]"
                          size="medium"
                          color="primary"
                        />
                        {crumb.name}
                      </div>
                    ),
                  }
                : crumb
            }
            isLastCrumb={idx === matchedPath.length - 1}
          />
          {idx !== matchedPath.length - 1 && (
            <Text variant="h4_t-20-700" color="secondary" className="mx-1.5">
              /
            </Text>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

const BreadcrumbItem = ({
  crumb,
  isLastCrumb,
}: {
  readonly crumb: Crumb;
  readonly isLastCrumb: boolean;
}) => {
  const route = crumb.search ? `${crumb.route}?${crumb.search}` : crumb.route;
  const text = (
    <Text variant="h4_t-20-700" color={isLastCrumb ? "primary" : "secondary"}>
      {crumb.name}
    </Text>
  );
  return isLastCrumb ? (
    text
  ) : (
    <Link to={route} data-testid="breadcrumb-item">
      {text}
    </Link>
  );
};

// recursively searches the breadcrumb tree to find the breadcrumb path that matches the current location
function generateFlatBreadcrumbPath(
  crumbs: Crumb[],
  currentPath: string
): Crumb[] {
  for (const crumb of crumbs) {
    // If the current crumb matches, return it
    if (crumb.route === currentPath) {
      return [crumb];
    }

    // If it has children, search recursively
    if (crumb.childRoutes) {
      const childPath = generateFlatBreadcrumbPath(
        crumb.childRoutes,
        currentPath
      );
      if (childPath.length > 0) {
        return [crumb, ...childPath];
      }
    }
  }

  // No match found
  return [];
}
